import { isInsightRecord } from './insightValue'

import type { UiTone } from '@/shared/ui'

export type AuthoringErrorKind = 'toast' | 'modal' | 'inline' | 'inline-validation' | 'fullpage'

export interface AuthoringErrorDescriptor {
  kind: AuthoringErrorKind
  tone: UiTone
  titleKey: string
  messageKey: string
  retryable: boolean
  backoffMs?: number
  target?: 'input' | 'preview' | 'workspace' | 'session'
  terminal?: boolean
  code: string | null
  status: number | null
  backendMessage: string | null
  details: unknown
  userVisible: boolean
}

interface AuthoringApiErrorLike {
  code?: string | null
  status?: number | null
  detail?: string
  details?: unknown
  payload?: unknown
  userVisible?: boolean
}

type AuthoringErrorTemplate = Omit<
  AuthoringErrorDescriptor,
  'code' | 'status' | 'backendMessage' | 'details' | 'userVisible'
>

const DEFAULT_TEMPLATE: AuthoringErrorTemplate = {
  kind: 'toast',
  tone: 'danger',
  titleKey: 'insights.sysadmin.authoring.error.unexpected.title',
  messageKey: 'insights.sysadmin.authoring.error.unexpected.message',
  retryable: false,
}

function template(
  key: string,
  options: Partial<AuthoringErrorTemplate> = {},
): AuthoringErrorTemplate {
  return {
    ...DEFAULT_TEMPLATE,
    titleKey: `insights.sysadmin.authoring.error.${key}.title`,
    messageKey: `insights.sysadmin.authoring.error.${key}.message`,
    ...options,
  }
}

const AUTHORING_ERROR_TEMPLATES: Readonly<Record<string, AuthoringErrorTemplate>> = {
  draft_llm_error: template('draftLlm', { retryable: true, backoffMs: 0 }),
  session_too_long: template('sessionTooLong', {
    kind: 'modal',
    tone: 'warning',
    terminal: true,
  }),
  draft_storage_unavailable: template('storageUnavailable', {
    tone: 'warning',
    retryable: true,
    backoffMs: 5_000,
  }),
  invalid_input: template('invalidInput', { kind: 'inline', target: 'input' }),
  draft_invalid: template('draftInvalid', {
    kind: 'inline-validation',
    tone: 'warning',
    target: 'preview',
  }),
  session_not_found: template('sessionExpired', {
    kind: 'modal',
    tone: 'warning',
    terminal: true,
  }),
  turn_not_found: template('turnExpired', {
    kind: 'modal',
    tone: 'warning',
    terminal: true,
  }),
  workspace_not_found: template('workspaceNotFound', { kind: 'inline', target: 'workspace' }),
  config_not_found: template('configNotFound', { kind: 'fullpage' }),
  draft_not_validated: template('dryRunFirst', { tone: 'warning' }),
  not_a_draft: template('alreadyActivated', { kind: 'fullpage', tone: 'warning' }),
  pipeline_error: template('pipelineFailed'),
}

function payloadDetails(payload: unknown): unknown {
  const root = isInsightRecord(payload) ? payload : null
  const envelope = isInsightRecord(root?.['error']) ? root['error'] : null
  return envelope?.['details'] ?? null
}

export function describeAuthoringError(
  error: AuthoringApiErrorLike = {},
): AuthoringErrorDescriptor {
  const code = error.code ?? null
  const selected = code ? AUTHORING_ERROR_TEMPLATES[code] : undefined
  const source = selected ?? DEFAULT_TEMPLATE
  return {
    ...source,
    code,
    status: error.status ?? null,
    backendMessage: error.detail?.trim() || null,
    details: error.details ?? payloadDetails(error.payload),
    userVisible: error.userVisible ?? true,
  }
}
